'use client';

import React, { forwardRef, ReactNode } from 'react';
import classnames from 'classnames';

import styles from './Box.module.scss';

type SpacingValue = 0 | 2 | 4 | 8 | 12 | 16 | 24 | 32 | 40 | 48 | 64 | 72 | 96 | 120 | 160;

type ResponsiveSpacing = {
  xs?: SpacingValue;
  sm?: SpacingValue;
  md?: SpacingValue;
  lg?: SpacingValue;
  xl?: SpacingValue;
  xxl?: SpacingValue;
};

type BackgroundColor =
  | 'black'
  | 'white'
  | 'gray-8'
  | 'gray-9'
  | 'gray-1'
  | 'product-sapphire-3'
  | 'gray-6';

type Direction = 'ltr' | 'rtl';
type Theme = 'dark' | 'light';

export interface BoxProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Background color */
  background?: BackgroundColor;
  /** className */
  className?: string;
  /** Margin Top */
  mt?: SpacingValue | ResponsiveSpacing;
  /** Margin Right */
  mr?: SpacingValue | ResponsiveSpacing;
  /** Margin Bottom */
  mb?: SpacingValue | ResponsiveSpacing;
  /** Margin Left */
  ml?: SpacingValue | ResponsiveSpacing;
  /** Padding Top */
  pt?: SpacingValue | ResponsiveSpacing;
  /** Padding Right */
  pr?: SpacingValue | ResponsiveSpacing;
  /** Padding Bottom */
  pb?: SpacingValue | ResponsiveSpacing;
  /** Padding Left */
  pl?: SpacingValue | ResponsiveSpacing;
  /** Test ID for testing */
  testId?: string;
  /** Text direction */
  direction?: Direction;
  /** Theme */
  theme?: Theme;
  /** Children */
  children?: ReactNode;
}

const Box = forwardRef<HTMLDivElement, BoxProps>(
  (
    {
      background = '',
      mt,
      mr,
      mb,
      ml,
      pt,
      pr,
      pb,
      pl,
      className,
      children,
      testId = 'box-container',
      direction = 'ltr',
      theme = 'dark',
      ...props
    },
    ref
  ) => {
    let classList = [styles.box];

    const updateSizing = (propertyName: string, value: SpacingValue | ResponsiveSpacing) => {
      let name = propertyName;
      if (direction === 'rtl') {
        if (propertyName === 'mr') name = 'ml';
        if (propertyName === 'ml') name = 'mr';
        if (propertyName === 'pr') name = 'pl';
        if (propertyName === 'pl') name = 'pr';
      }

      if (typeof value === 'number') {
        classList = [...classList, styles[`${name}-${value}`]];
      } else {
        for (const size in value) {
          const breakpoint = size as keyof ResponsiveSpacing;
          const spacingValue = value[breakpoint];
          if (spacingValue !== undefined) {
            classList = [...classList, styles[`${name}-${size}-${spacingValue}`]];
          }
        }
      }
    };

    // Margins
    mt && updateSizing('mt', mt);
    mr && updateSizing('mr', mr);
    mb && updateSizing('mb', mb);
    ml && updateSizing('ml', ml);

    // Paddings
    pt && updateSizing('pt', pt);
    pr && updateSizing('pr', pr);
    pb && updateSizing('pb', pb);
    pl && updateSizing('pl', pl);

    return (
      <div
        className={classnames(
          classList,
          {
            [styles[`${theme}-background-${background}`]]: background !== '',
          },
          className
        )}
        ref={ref}
        data-testid={testId}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Box.displayName = 'Box';

export default Box;
